import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useAuthForm } from '../components/ui/modals/UserModal/hooks/useAuthForm';
import { useAuthValidation } from '../components/ui/modals/UserModal/hooks/useAuthValidation';
import { AuthForm } from '../components/ui/modals/UserModal/components/AuthForm';
import { AuthErrorDisplay } from '../components/ui/modals/UserModal/components/AuthErrorDisplay';
import { TermsModal } from '../components/ui/modals/TermsModal';
import { EmailVerificationModal } from '../components/ui/modals/EmailVerificationModal';

export const NativeLoginPage: React.FC = () => {
  const {
    session,
    loading: authLoading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithApple,
    resendVerificationEmail,
    checkUsernameAvailability,
    checkEmailAvailability,
    checkEmailConfirmationStatus,
    acceptTerms,
  } = useAuth();

  const { theme } = useTheme();

  const {
    emailValid,
    emailAvailable,
    checkingEmail,
    setEmailAvailable,
    checkEmail,
    usernameAvailable,
    setUsernameAvailable,
    resetEmailValidation,
  } = useAuthValidation({
    checkUsernameAvailability,
    checkEmailAvailability,
  });

  const authForm = useAuthForm({
    session,
    authActions: {
      signIn,
      signUp,
      signOut,
      signInWithGoogle,
      signInWithApple,
      resendVerificationEmail,
      checkUsernameAvailability,
      checkEmailConfirmationStatus,
      acceptTerms,
    },
    isOpen: true,
    theme,
    skipCaptcha: true,
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    authForm.handleEmailAuth(
      e,
      emailAvailable,
      setEmailAvailable,
      usernameAvailable,
      setUsernameAvailable
    );
  };

  const handleCaptchaSuccess = (token: string) => {
    authForm.setCaptchaToken(token);
    if (authForm.error === 'Please complete the CAPTCHA challenge') {
      authForm.setError('');
    }
  };

  const handleCaptchaError = () => {
    authForm.setCaptchaToken(null);
  };

  const handleCaptchaExpire = () => {
    authForm.setCaptchaToken(null);
  };

  const noop = () => {};

  return (
    <div
      className="min-h-screen bg-background flex flex-col items-center justify-center px-4"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Branding */}
      <div className="flex flex-col items-center mb-6">
        <img
          src="/assets/site-logo/cropped-neutral-logo.png"
          alt="Wallstreetle"
          className="w-20 h-20 mb-3"
        />
        <h1 className="text-2xl font-bold text-foreground">Wallstreetle</h1>
        <p className="text-sm text-muted-foreground">Daily Stock Guessing Game</p>
      </div>

      {/* Auth Form */}
      <div className="w-full max-w-sm">
        <AuthForm
          mode={authForm.mode}
          email={authForm.email}
          password={authForm.password}
          confirmPassword={authForm.confirmPassword}
          username={authForm.username}
          authLoading={authLoading}
          theme={theme}
          onEmailChange={authForm.setEmail}
          onPasswordChange={authForm.setPassword}
          onConfirmPasswordChange={authForm.setConfirmPassword}
          onUsernameChange={authForm.setUsername}
          showPassword={authForm.showPassword}
          showConfirmPassword={authForm.showConfirmPassword}
          onToggleShowPassword={() => authForm.setShowPassword(!authForm.showPassword)}
          onToggleShowConfirmPassword={() => authForm.setShowConfirmPassword(!authForm.showConfirmPassword)}
          checkingEmail={checkingEmail}
          emailValid={emailValid}
          emailAvailable={emailAvailable}
          onCheckEmail={checkEmail}
          shouldRenderCaptcha={authForm.shouldRenderCaptcha}
          captchaResetCounter={authForm.captchaResetCounter}
          onCaptchaSuccess={handleCaptchaSuccess}
          onCaptchaError={handleCaptchaError}
          onCaptchaExpire={handleCaptchaExpire}
          termsAccepted={authForm.termsAccepted}
          termsError={authForm.termsError}
          onTermsChange={(checked) => {
            authForm.setTermsAccepted(checked);
            authForm.setTermsError('');
          }}
          onViewTerms={() => authForm.setShowTermsModal(true)}
          successMessage={authForm.successMessage}
          hasPendingCheckout={authForm.hasPendingCheckout}
          googleButtonLabel={authForm.googleButtonLabel}
          appleButtonLabel={authForm.appleButtonLabel}
          onSubmit={handleFormSubmit}
          onGoogleLogin={authForm.handleGoogleLogin}
          onAppleLogin={authForm.handleAppleLogin}
          onModeSwitch={() => authForm.handleModeSwitch(resetEmailValidation)}
          onClose={noop}
        />

        <AuthErrorDisplay
          error={authForm.error}
          errorType={authForm.errorType}
          recoverySuggestions={authForm.recoverySuggestions}
          showLoginSuggestion={authForm.showLoginSuggestion}
          showResendVerification={authForm.showResendVerification}
          resendingVerification={authForm.resendingVerification}
          onTryLogin={authForm.handleTryLogin}
          onResendVerification={authForm.handleResendVerification}
        />
      </div>

      {/* Sub-modals */}
      <TermsModal
        isOpen={authForm.showTermsModal}
        onClose={() => authForm.setShowTermsModal(false)}
        showAcceptButton={false}
      />

      <EmailVerificationModal
        isOpen={authForm.showEmailVerificationModal}
        onClose={() => authForm.setShowEmailVerificationModal(false)}
        email={authForm.verificationEmail}
      />
    </div>
  );
};
